import React, { useState } from 'react';
import {
  Box, Typography, Paper, Grid, Chip, Button, Table, TableBody,
  TableCell, TableHead, TableRow, TableContainer, LinearProgress,
  Tabs, Tab, Alert, Switch, FormControlLabel, Divider, IconButton,
  Card, CardContent, CardHeader, Avatar, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField,
} from '@mui/material';
import {
  PhoneAndroid as MobileIcon,
  AccountBalance as BankIcon,
  ElectricBolt as UtilityIcon,
  Store as MerchantIcon,
  Wifi as TelecomIcon,
  Agriculture as AgriIcon,
  Security as ConsentIcon,
  SyncAlt as SyncIcon,
  CheckCircle as ConnectedIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  DataObject as DataIcon,
  CloudUpload as IngestIcon,
  ManageSearch as ManageIcon,
  Shield as ShieldIcon,
} from '@mui/icons-material';

interface DataSource {
  id: string;
  name: string;
  category: string;
  type: 'API' | 'Batch' | 'Real-time';
  status: 'Connected' | 'Error' | 'Pending' | 'Inactive';
  lastSync: string;
  recordsToday: number;
  coverage: number;
  consentRequired: boolean;
}

const dataSources: DataSource[] = [
  { id: 'DS001', name: 'RBZ Credit Registry', category: 'Credit Bureau', type: 'API', status: 'Connected', lastSync: '2026-03-02 06:00', recordsToday: 1240, coverage: 95, consentRequired: true },
  { id: 'DS002', name: 'EcoCash Mobile Money', category: 'Mobile & Wallets', type: 'Real-time', status: 'Connected', lastSync: '2026-03-02 09:47', recordsToday: 8420, coverage: 88, consentRequired: true },
  { id: 'DS003', name: 'NetOne Telecom Data', category: 'Telecom', type: 'API', status: 'Connected', lastSync: '2026-03-02 08:00', recordsToday: 3100, coverage: 72, consentRequired: true },
  { id: 'DS004', name: 'ZESA Electricity Payments', category: 'Utilities', type: 'Batch', status: 'Connected', lastSync: '2026-03-02 03:00', recordsToday: 2800, coverage: 65, consentRequired: false },
  { id: 'DS005', name: 'DStv / Multichoice', category: 'Utilities', type: 'Batch', status: 'Pending', lastSync: '2026-03-01 03:00', recordsToday: 0, coverage: 40, consentRequired: false },
  { id: 'DS006', name: 'TelOne Broadband', category: 'Utilities', type: 'API', status: 'Connected', lastSync: '2026-03-02 07:30', recordsToday: 920, coverage: 55, consentRequired: false },
  { id: 'DS007', name: 'Innbucks POS Transactions', category: 'Digital Payments', type: 'Real-time', status: 'Connected', lastSync: '2026-03-02 09:50', recordsToday: 5600, coverage: 70, consentRequired: true },
  { id: 'DS008', name: 'ZimSwitch Bank Statements', category: 'Banking', type: 'API', status: 'Connected', lastSync: '2026-03-02 06:00', recordsToday: 3900, coverage: 80, consentRequired: true },
  { id: 'DS009', name: 'ZIMRA Tax Records', category: 'Tax / Compliance', type: 'Batch', status: 'Error', lastSync: '2026-02-28 03:00', recordsToday: 0, coverage: 30, consentRequired: true },
  { id: 'DS010', name: 'Agro-dealer Input Purchase', category: 'Agriculture', type: 'Batch', status: 'Connected', lastSync: '2026-03-02 05:00', recordsToday: 480, coverage: 35, consentRequired: false },
  { id: 'DS011', name: 'Weather & Climate API', category: 'Agriculture', type: 'Real-time', status: 'Connected', lastSync: '2026-03-02 09:00', recordsToday: 24, coverage: 100, consentRequired: false },
  { id: 'DS012', name: 'Device Fingerprinting SDK', category: 'Behavioral', type: 'Real-time', status: 'Connected', lastSync: '2026-03-02 09:55', recordsToday: 6200, coverage: 82, consentRequired: true },
];

const categoryIcons: Record<string, JSX.Element> = {
  'Credit Bureau': <BankIcon />,
  'Mobile & Wallets': <MobileIcon />,
  'Telecom': <TelecomIcon />,
  'Utilities': <UtilityIcon />,
  'Digital Payments': <MerchantIcon />,
  'Banking': <BankIcon />,
  'Tax / Compliance': <ShieldIcon />,
  'Agriculture': <AgriIcon />,
  'Behavioral': <DataIcon />,
};

const categoryColors: Record<string, string> = {
  'Credit Bureau': '#1565c0',
  'Mobile & Wallets': '#7b1fa2',
  'Telecom': '#0097a7',
  'Utilities': '#0891b2',
  'Digital Payments': '#2e7d32',
  'Banking': '#1565c0',
  'Tax / Compliance': '#5d4037',
  'Agriculture': '#558b2f',
  'Behavioral': '#c62828',
};

const alternativeDataCategories = [
  {
    icon: <MobileIcon />, color: '#7b1fa2', title: 'Mobile & Telecom Data',
    items: ['Mobile money transaction history (EcoCash, Mukuru)', 'Airtime top-up frequency and amounts', 'Call/SMS activity patterns (metadata only)', 'SIM tenure and device consistency'],
    coverage: 88,
  },
  {
    icon: <MerchantIcon />, color: '#2e7d32', title: 'Digital Payments & Wallets',
    items: ['Merchant wallet inflows/outflows', 'Transaction frequency and ticket size', 'Payment reversals and chargebacks', 'POS and QR transaction behavior'],
    coverage: 70,
  },
  {
    icon: <UtilityIcon />, color: '#0891b2', title: 'Utility & Bill Payments',
    items: ['ZESA electricity payments (prepaid/postpaid)', 'Water authority bill payments', 'Pay-TV (DStv/OpenView) subscriptions', 'TelOne/Broadband internet payments'],
    coverage: 60,
  },
  {
    icon: <DataIcon />, color: '#c62828', title: 'Behavioral & Device Data',
    items: ['Device fingerprinting (unique device ID)', 'App usage consistency and session patterns', 'Location stability (non-GPS, cell tower)', 'Login patterns and session behavior'],
    coverage: 82,
  },
  {
    icon: <MerchantIcon />, color: '#0d47a1', title: 'SME-Specific Alternative Data',
    items: ['Inventory turnover from POS systems', 'Supplier payment regularity', 'Invoice history via e-invoicing platforms', 'Sales velocity and seasonal revenue patterns'],
    coverage: 55,
  },
  {
    icon: <AgriIcon />, color: '#558b2f', title: 'Agriculture & Rural Data',
    items: ['Crop cycles and harvest season calendars', 'Agro-dealer input purchase history', 'Weather & climate risk overlays (Zimbabwe Met)', 'Off-taker contracts and produce agreements'],
    coverage: 35,
  },
];

const getStatusColor = (status: string) => {
  if (status === 'Connected') return 'success';
  if (status === 'Error') return 'error';
  if (status === 'Pending') return 'warning';
  return 'default';
};

const getStatusIcon = (status: string) => {
  if (status === 'Connected') return <ConnectedIcon fontSize="small" />;
  if (status === 'Error') return <ErrorIcon fontSize="small" />;
  return <PendingIcon fontSize="small" />;
};

export default function DataIngestionPage() {
  const [tab, setTab] = useState(0);
  const [consentDialogOpen, setConsentDialogOpen] = useState(false);

  const connected = dataSources.filter(d => d.status === 'Connected').length;
  const totalRecords = dataSources.reduce((sum, d) => sum + d.recordsToday, 0);
  const avgCoverage = Math.round(dataSources.reduce((sum, d) => sum + d.coverage, 0) / dataSources.length);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={800} color="primary" gutterBottom>
          Data Ingestion & Alternative Data
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Multi-source ingestion · Real-time & batch pipelines · Consent management · Zimbabwe-specific data sources
        </Typography>
      </Box>

      {/* Summary KPIs */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Connected Sources', value: `${connected} / ${dataSources.length}`, color: '#16a34a', bg: '#f0fdf4' },
          { label: 'Records Ingested Today', value: totalRecords.toLocaleString('en-ZW'), color: '#1565c0', bg: '#eff6ff' },
          { label: 'Avg. Population Coverage', value: `${avgCoverage}%`, color: '#7b1fa2', bg: '#faf5ff' },
          { label: 'Data Sources with Errors', value: dataSources.filter(d => d.status === 'Error').length, color: '#dc2626', bg: '#fef2f2' },
        ].map(k => (
          <Grid size={{ xs: 6, md: 3 }} key={k.label}>
            <Paper sx={{ p: 2.5, bgcolor: k.bg, border: `1px solid ${k.color}22`, borderRadius: 2 }}>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>{k.label}</Typography>
              <Typography variant="h4" fontWeight={800} sx={{ color: k.color, mt: 0.5 }}>{k.value}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#f8fafc' }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<SyncIcon />} iconPosition="start" label="Data Sources" />
          <Tab icon={<DataIcon />} iconPosition="start" label="Alternative Data" />
          <Tab icon={<ConsentIcon />} iconPosition="start" label="Consent Management" />
          <Tab icon={<ManageIcon />} iconPosition="start" label="Data Quality" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* Tab 0: Data Sources */}
          {tab === 0 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>Active Data Source Connections</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="outlined" size="small" startIcon={<SyncIcon />}>Sync All</Button>
                  <Button variant="contained" size="small" startIcon={<IngestIcon />}>Add Source</Button>
                </Box>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                    <TableRow>
                      <TableCell><strong>Source</strong></TableCell>
                      <TableCell><strong>Category</strong></TableCell>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Last Sync</strong></TableCell>
                      <TableCell><strong>Records Today</strong></TableCell>
                      <TableCell><strong>Population Coverage</strong></TableCell>
                      <TableCell><strong>Consent</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dataSources.map(ds => (
                      <TableRow key={ds.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 28, height: 28, bgcolor: `${categoryColors[ds.category]}22`, color: categoryColors[ds.category] }}>
                              {React.cloneElement(categoryIcons[ds.category] || <DataIcon />, { style: { fontSize: 16 } })}
                            </Avatar>
                            <Typography variant="body2" fontWeight={600}>{ds.name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={ds.category} size="small" sx={{ bgcolor: `${categoryColors[ds.category]}15`, color: categoryColors[ds.category], fontWeight: 600 }} />
                        </TableCell>
                        <TableCell>
                          <Chip label={ds.type} size="small" variant="outlined"
                            color={ds.type === 'Real-time' ? 'success' : ds.type === 'API' ? 'primary' : 'default'} />
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(ds.status)}
                            label={ds.status}
                            size="small"
                            color={getStatusColor(ds.status) as any}
                            variant="filled"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color={ds.status === 'Error' ? 'error' : 'text.secondary'}>{ds.lastSync}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight={600} color={ds.recordsToday > 0 ? 'text.primary' : 'text.disabled'}>
                            {ds.recordsToday.toLocaleString('en-ZW')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 100 }}>
                            <LinearProgress
                              variant="determinate"
                              value={ds.coverage}
                              sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: ds.coverage >= 70 ? '#16a34a' : ds.coverage >= 50 ? '#d97706' : '#dc2626' } }}
                            />
                            <Typography variant="caption">{ds.coverage}%</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {ds.consentRequired
                            ? <Chip label="Required" size="small" color="primary" variant="outlined" />
                            : <Chip label="Not Required" size="small" variant="outlined" />
                          }
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {dataSources.some(d => d.status === 'Error') && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <strong>ZIMRA Tax Records</strong> connection has failed. Last successful sync: 2026-02-28. Please check API credentials or contact the data provider.
                </Alert>
              )}
            </Box>
          )}

          {/* Tab 1: Alternative Data */}
          {tab === 1 && (
            <Box>
              <Typography variant="h6" fontWeight={700} gutterBottom>Alternative Data Sources — Zimbabwe Context</Typography>
              <Alert severity="info" sx={{ mb: 3 }}>
                Alternative data is critical for scoring thin-file and no-file borrowers in Zimbabwe. These sources supplement or replace traditional credit bureau data for the unbanked and underbanked population.
              </Alert>
              <Grid container spacing={2}>
                {alternativeDataCategories.map(cat => (
                  <Grid size={{ xs: 12, md: 6 }} key={cat.title}>
                    <Card sx={{ height: '100%', border: `1px solid ${cat.color}22` }}>
                      <CardHeader
                        avatar={<Avatar sx={{ bgcolor: cat.color }}>{cat.icon}</Avatar>}
                        title={<Typography fontWeight={700}>{cat.title}</Typography>}
                        action={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
                            <Typography variant="caption" color="text.secondary">Coverage</Typography>
                            <Chip
                              label={`${cat.coverage}%`}
                              size="small"
                              sx={{ bgcolor: cat.coverage >= 70 ? '#f0fdf4' : cat.coverage >= 50 ? '#fffbeb' : '#fef2f2', color: cat.coverage >= 70 ? '#16a34a' : cat.coverage >= 50 ? '#d97706' : '#dc2626', fontWeight: 700 }}
                            />
                          </Box>
                        }
                        sx={{ bgcolor: `${cat.color}08`, borderBottom: `1px solid ${cat.color}22` }}
                      />
                      <CardContent>
                        <LinearProgress
                          variant="determinate"
                          value={cat.coverage}
                          sx={{ mb: 1.5, height: 6, borderRadius: 3, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: cat.color } }}
                        />
                        {cat.items.map((item, i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                            <ConnectedIcon sx={{ fontSize: 14, color: cat.color, mt: 0.3, flexShrink: 0 }} />
                            <Typography variant="body2">{item}</Typography>
                          </Box>
                        ))}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Tab 2: Consent Management */}
          {tab === 2 && (
            <Box>
              <Typography variant="h6" fontWeight={700} gutterBottom>Consent & Data Privacy Management</Typography>
              <Alert severity="success" sx={{ mb: 3 }}>
                All data collection complies with Zimbabwe's Data Protection Act (2021) and the Reserve Bank of Zimbabwe's consumer data guidelines.
              </Alert>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper sx={{ p: 2.5 }}>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom color="primary">Consent Scopes</Typography>
                    {[
                      { scope: 'Credit Bureau Data', required: true, active: true, description: 'RBZ registry access for credit history' },
                      { scope: 'Mobile Money History', required: true, active: true, description: 'EcoCash/Mukuru transaction metadata (last 12 months)' },
                      { scope: 'Telecom Usage Patterns', required: true, active: true, description: 'Airtime, data, SIM metadata — no call content' },
                      { scope: 'Bank Statement Analysis', required: true, active: true, description: 'ZimSwitch bank transaction history' },
                      { scope: 'Behavioral / Device Data', required: false, active: true, description: 'Anonymous device fingerprint and session stability' },
                      { scope: 'Utility Payments', required: false, active: false, description: 'ZESA, water, pay-TV payment history' },
                    ].map(c => (
                      <Box key={c.scope} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', py: 1.5, borderBottom: '1px dashed #e2e8f0' }}>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight={600}>{c.scope}</Typography>
                            {c.required && <Chip label="Required" size="small" color="primary" sx={{ height: 18, fontSize: '0.65rem' }} />}
                          </Box>
                          <Typography variant="caption" color="text.secondary">{c.description}</Typography>
                        </Box>
                        <Switch checked={c.active} size="small" disabled={c.required} />
                      </Box>
                    ))}
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper sx={{ p: 2.5 }}>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom color="primary">Data Retention Policies</Typography>
                    {[
                      { category: 'Credit Applications', retention: '7 years', basis: 'Regulatory requirement' },
                      { category: 'Alternative Data (raw)', retention: '24 months', basis: 'Scoring model inputs' },
                      { category: 'Behavioral Signals', retention: '6 months', basis: 'Pattern analysis window' },
                      { category: 'Decision Logs', retention: '10 years', basis: 'Audit and compliance' },
                      { category: 'Consent Records', retention: 'Indefinite', basis: 'Data Protection Act' },
                      { category: 'KYC Documents', retention: '5 years post-relationship', basis: 'AML regulations' },
                    ].map(r => (
                      <Box key={r.category} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px dashed #e2e8f0' }}>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{r.category}</Typography>
                          <Typography variant="caption" color="text.secondary">{r.basis}</Typography>
                        </Box>
                        <Chip label={r.retention} size="small" variant="outlined" color="primary" />
                      </Box>
                    ))}
                  </Paper>
                  <Paper sx={{ p: 2.5, mt: 2, bgcolor: '#f0fdf4', border: '1px solid #16a34a22' }}>
                    <Typography variant="subtitle2" fontWeight={700} color="success.dark" gutterBottom>Compliance Certifications</Typography>
                    {['Zimbabwe Data Protection Act (2021)', 'RBZ Consumer Data Guidelines', 'ISO 27001 Information Security', 'SOC 2 Type II (Pending)'].map(c => (
                      <Box key={c} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <ConnectedIcon sx={{ fontSize: 14, color: '#16a34a' }} />
                        <Typography variant="body2">{c}</Typography>
                      </Box>
                    ))}
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Tab 3: Data Quality */}
          {tab === 3 && (
            <Box>
              <Typography variant="h6" fontWeight={700} gutterBottom>Data Normalization & Quality Monitoring</Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Raw data from heterogeneous sources is normalized, enriched with derived metrics, and validated before entering the scoring pipeline. Missing or sparse data is handled with imputation strategies.
              </Alert>
              <Grid container spacing={3}>
                {[
                  { title: 'Data Completeness', value: 87, color: '#16a34a', desc: 'Average completeness across all active sources. Fields with >30% missing values trigger imputation.' },
                  { title: 'Schema Consistency', value: 94, color: '#1565c0', desc: 'Cross-source schema validation. Mismatched field formats are auto-normalised to platform schema.' },
                  { title: 'Freshness Score', value: 79, color: '#d97706', desc: 'Percentage of records updated within the expected cadence. Stale data flagged for re-ingestion.' },
                  { title: 'Anomaly Rate', value: 3, color: '#dc2626', desc: '3% of ingested records flagged as statistical outliers. Reviewed before scoring inclusion.' },
                ].map(q => (
                  <Grid size={{ xs: 12, md: 6 }} key={q.title}>
                    <Paper sx={{ p: 2.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography fontWeight={700}>{q.title}</Typography>
                        <Typography fontWeight={800} sx={{ color: q.color, fontSize: '1.4rem' }}>
                          {q.title === 'Anomaly Rate' ? `${q.value}%` : `${q.value}%`}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={q.title === 'Anomaly Rate' ? 100 - q.value : q.value}
                        sx={{ height: 8, borderRadius: 4, bgcolor: '#e2e8f0', mb: 1.5, '& .MuiLinearProgress-bar': { bgcolor: q.color } }}
                      />
                      <Typography variant="body2" color="text.secondary">{q.desc}</Typography>
                    </Paper>
                  </Grid>
                ))}
                <Grid size={{ xs: 12 }}>
                  <Paper sx={{ p: 2.5 }}>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>Derived Metrics & Enrichment</Typography>
                    <Grid container spacing={2}>
                      {[
                        { label: 'Income Proxy Score', desc: 'Estimated monthly income from mobile money inflows + POS activity', active: true },
                        { label: 'Behavioral Stability Index', desc: 'Consistency of transaction patterns over 90-day rolling window', active: true },
                        { label: 'Cash Flow Ratio (SME)', desc: 'Inflows vs outflows ratio for merchant accounts — seasonal adjustment applied', active: true },
                        { label: 'Digital Footprint Score', desc: 'Depth and breadth of digital transaction history', active: true },
                        { label: 'Seasonal Risk Overlay', desc: 'Agricultural borrowers: risk adjustment based on crop cycle and climate data', active: false },
                        { label: 'Social Network Score', desc: 'Group lending — shared risk across connected business entities', active: false },
                      ].map(m => (
                        <Grid size={{ xs: 12, md: 6 }} key={m.label}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
                            <Box>
                              <Typography variant="body2" fontWeight={600}>{m.label}</Typography>
                              <Typography variant="caption" color="text.secondary">{m.desc}</Typography>
                            </Box>
                            <Chip label={m.active ? 'Active' : 'Planned'} size="small" color={m.active ? 'success' : 'default'} />
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
